<script setup lang="ts">
import {
  breakpointsTailwind, onClickOutside,
  useBreakpoints, useDraggable, useEventListener, useIntervalFn,
  useTimeoutFn,
  useWindowSize,
  whenever, } from '@vueuse/core';
import { secondsToMilliseconds } from 'date-fns';
import { clamp, throttle } from 'lodash-es';
import { computed, nextTick, onMounted, ref, watch } from 'vue';
import InlineSvg from 'vue-inline-svg';

import ICON_AUDIO_OFF from '@/assets/icons/audio-off.svg';
import ICON_AUDIO_ON from '@/assets/icons/audio-on.svg';
import ICON_PAUSE from '@/assets/icons/pause.svg';
import ICON_PLAY from '@/assets/icons/play.svg';
import { useBody } from '@/composables/useBody';
import { onChapterClickedToggle } from '@/composables/useChapterClickedHook';
import { usePostMessageReceiver } from '@/composables/usePostMessageReceiver';
import { useScrollLocker } from '@/composables/useScrollLocker';
import { $ } from '@/js/browser';
import { STREAM_BASE_URI } from '@/js/constants';
import { globalConfig } from '@/store/globalConfig';
import {
  canStreamPostMessages,
  requestMute, requestPip, requestPlay, requestSeek,
} from '@/store/streamPostMessenger';
import { useTemplate } from '@/store/templateData';
import { browserStorageFingerprintProId } from '@/store-composable/fingerprintProId';
import { useExpirableLocalStorage } from '@/store-composable/useExpirableLocalStorage';
import * as UrlQueryConsumer from '@/utils/runtime/urlQueryConsumer';
import { KNOWN_CONSUMABLES } from '@/utils/runtime/urlQueryConsumer';
import { toUrlObjectOrNull } from '@/utils/transformer/url';
import IconFa6SolidSpinner from '~icons/fa6-solid/spinner';
import IconHumbleiconsTimes from '~icons/humbleicons/times';

import ICON_BACK_TO_STREAM_ROOM from '../../assets/icons/icon-back-to-stream-room.svg';

const INITIAL_POSITION_RIGHT_OFFSET = 10;
const INITIAL_POSITION_BOTTOM_OFFSET = 10;

const DELAY_POST_SUB_VIEW_FALSE = 300;

const RECAPTURE_STREAM_ELEMENT_TIME = 1000;

// 跟 ＠/components/StreamPip/StreamPip.scss 的設定相同，若要修改要同時修改兩邊
const SUB_STREAM_WIDTH_SM = 150;
const SUB_STREAM_HEIGHT_SM = 263;

const SUB_STREAM_WIDTH_MD = 180;
const SUB_STREAM_HEIGHT_MD = 315;

const REVOKE_HAS_MOVED_MILLISECONDS = 300;

const breakpoints = useBreakpoints(breakpointsTailwind);
const isScreenGreaterThanMedium = breakpoints.greater('md');
const subStreamSize = computed(() => {
  const height = isScreenGreaterThanMedium.value ? SUB_STREAM_HEIGHT_MD : SUB_STREAM_HEIGHT_SM;
  const width = isScreenGreaterThanMedium.value ? SUB_STREAM_WIDTH_MD : SUB_STREAM_WIDTH_SM;
  return { height, width };
});
const {
  onStreamReady, onAudioStatusChanged, onPlayStatusChanged,
  onSelectedClipChanged, onVideoCurrentTimeChanged,
} = usePostMessageReceiver();

const {
  handsupConfig,
  isGeneralCmsStreamCanPlay,
  isModuleIncludingStreamRoom,
  isModuleIncludingUpcomingLiveFab,
  isOnLive,
  isReplaying,
  isUpcoming,
  isClipArchive,
} = useTemplate();

// 控制切換 `主要 Stream` 或是 `PIP`
const shouldShowSubStreamRoom = ref(false);
// 控制使用者有沒有關掉 PIP
const isSubStreamHide = ref(false);

const postId = computed(() => handsupConfig.value?.post?.id);
const slug = computed(() => handsupConfig.value?.store?.slug);
const liveRoomUrl = computed(() => {
  if (!postId.value || !slug.value) { return ''; }
  if (isSubStreamHide.value && globalConfig.value.isPipOnly) { return ''; }

  const url = `${STREAM_BASE_URI}/${slug.value}/live/${postId.value}`;
  const urlObject = toUrlObjectOrNull(url);
  if (urlObject === null) {
    console.error(`live room not load since url is illegal: ${url}`);
    return '';
  }

  urlObject.searchParams.set('hu_source', 'cms_sdk');

  // NOTE: 將 browserStorage 中的 fingerprintProId 帶給 Buyer，節省 Fingerprint Pro API 請求次數
  // REF: https://17media.atlassian.net/browse/HU-7583
  if (browserStorageFingerprintProId.value) {
    urlObject.searchParams.set('fp_id', browserStorageFingerprintProId.value);
  }

  return urlObject.toString();
});

const needRender = computed(() => [
  isGeneralCmsStreamCanPlay.value,
  Boolean(liveRoomUrl.value),
  /**
   * render PIP 有兩個情況
   * 1. PIP_ONLY: true
   * 2. 若 PIP_ONLY 為 false, 則一定要有 stream room 模組才 render PIP
   **/
  globalConfig.value.isPipOnly || isModuleIncludingStreamRoom.value,
].every(Boolean));

const wrapperEl = ref<HTMLElement | null>(null);
const streamEl = ref<HTMLElement | null>(null);
const hasMoved = ref(false);
const isSubStreamPositionInitial = ref(false);

const isShowMainStreamRoom = computed(() => (
  !shouldShowSubStreamRoom.value &&
  canStreamPostMessages.value &&
  streamEl.value
));
const isShowSubStreamRoom = computed(() => (
  shouldShowSubStreamRoom.value &&
  canStreamPostMessages.value &&
  !isSubStreamHide.value &&
  isSubStreamPositionInitial.value
));
const windowSize = useWindowSize();
const { bodySize, isBodyMounted } = useBody();
const dragStartPos = ref<{ x: number; y: number } | null>(null);
const wrapperDraggable = ref(useDraggable(wrapperEl, {
  preventDefault: true,
  onStart: (position, e) => {
    dragStartPos.value = {
      x: e.x,
      y: e.y,
    };
  },
  onMove: (position, e) => {
    // NOTE: 當拖移視窗時，擋掉其他一起觸發的事件(如：關掉視窗，開關聲音)
    if (!dragStartPos.value) { return; }
    
    const xDelta = dragStartPos.value.x - e.x;
    const yDelta = dragStartPos.value.y - e.y;
    const moveDistance = Math.hypot(xDelta, yDelta);
    if (moveDistance >= 5) {
      hasMoved.value = true;
    }
  },
  onEnd: () => {
    // NOTE: 必須依靠 setTimeout 的方式將阻檔事件的 flag 收回，因為 onEnd 是由 pointerup
    // 觸發，若直接收回會造成無法阻擋到 click 的事件觸發
    setTimeout(() => {
      hasMoved.value = false;
    }, REVOKE_HAS_MOVED_MILLISECONDS);
  },
}));
const streamElBounding = ref<{ x: number; y: number; width: number; height: number } | null>(null);
/**
 * NOTE: 此 method 希望可以取得目前 stream wrapper 的絕對 x, y, 不過會受到一些
 * margin 設定的影響，目前不確定 iframe 或多層 scroll 下會不會有計算問題，目前解法
 * 參考以下連結提供的方式
 * REF: https://stackoverflow.com/a/28222246
 */
const setStreamAbsolutePosition = () => {
  if (!streamEl.value) { return; }
  // NOTE: 如果 body 是 relative，streamEl 會以 body 作為參照點，所以要減去 body 的 top，反之則不用
  const isBodyRelative = getComputedStyle(document.body).position === 'relative';
  const elemRect = streamEl.value.getBoundingClientRect();
  const bodyRect = document.body.getBoundingClientRect();
  const offsetTop = isBodyRelative ? (elemRect.top - bodyRect.top) : (elemRect.top + window.scrollY);
  const offsetLeft = isBodyRelative ? (elemRect.left - bodyRect.left) : (elemRect.left + window.scrollX);

  streamElBounding.value = {
    x: offsetLeft,
    y: offsetTop,
    width: elemRect.width,
    height: elemRect.height,
  };
};

// 監控 Stream Room Box 是否顯示在螢幕上決定是否顯示 Sub Stream Room，無論畫面是在 Stream Room 的上方或下方
// NOTE: useIntersectionObserver 沒有提供 stop 後又重新 observe 的方法，因此用原生的 IntersectionObserver api
const streamRoomIntersectionObserver = new IntersectionObserver((entries) => {
  const isStreamRoomShow = entries[0].isIntersecting;
  shouldShowSubStreamRoom.value = !isStreamRoomShow;
  if (!isStreamRoomShow) {
    // 重置是否隱藏 subStream 的狀態
    isSubStreamHide.value = false;
  }
});

const isShowUpcomingLiveFab = computed(() => (
  isModuleIncludingUpcomingLiveFab.value &&
  isUpcoming.value &&
  !globalConfig.value.isPipOnly
));
// 依 UpcomingLiveFab 決定是否監控 Stream Room Box
watch(isShowUpcomingLiveFab, () => {
  if (!streamEl.value) { return; }

  if (isShowUpcomingLiveFab.value) {
    shouldShowSubStreamRoom.value = false;
    streamRoomIntersectionObserver.unobserve(streamEl.value);
  } else {
    streamRoomIntersectionObserver.observe(streamEl.value);
  }
});

// 因為不確定 streamEl 何時產生，所以用 interval 嘗試到找到 streamEl 為止
const intervalPause = useIntervalFn(() => nextTick(() => {
  const config = globalConfig.value;
  if (config.isPipOnly) {
    shouldShowSubStreamRoom.value = true;
    intervalPause.pause();
    return;
  }

  const streamElId = config.streamRoomMountPointId ?? config.localStreamId;
  const el = $(`#${streamElId}`);
  if (el) {
    streamEl.value = el as HTMLElement;
    setStreamAbsolutePosition();

    if (!isShowUpcomingLiveFab.value && streamEl.value) {
      streamRoomIntersectionObserver.observe(streamEl.value);
    }

    intervalPause.pause();
  }
})
, RECAPTURE_STREAM_ELEMENT_TIME, { immediateCallback: true });
watch(() => bodySize, () => setStreamAbsolutePosition(), { immediate: true, deep: true });
useEventListener(window, 'scroll', setStreamAbsolutePosition);

// 處理與 stream 訊息的傳送
watch(shouldShowSubStreamRoom, () => {
  if (needRender.value && canStreamPostMessages.value) {
    // 因為太早 call 的話 stream 會在 PIP 還沒消失前就先將隱藏的東西顯示，造成畫面閃爍
    setTimeout(() => {
      requestPip(shouldShowSubStreamRoom.value);
    }, shouldShowSubStreamRoom.value ? 0 : DELAY_POST_SUB_VIEW_FALSE);
  }
}, { immediate: true });

onStreamReady(() => {
  if (needRender.value) { requestPip(shouldShowSubStreamRoom.value); }
});

// 用來判斷 sub stream 位置初始化後才顯示用
const initSubStreamPosition = () => {
  if (!wrapperDraggable.value?.position) {
    return;
  }

  const x = bodySize.width.value - subStreamSize.value.width - INITIAL_POSITION_RIGHT_OFFSET;
  const y = windowSize.height.value - subStreamSize.value.height - INITIAL_POSITION_BOTTOM_OFFSET;
  wrapperDraggable.value.position.x = x;
  wrapperDraggable.value.position.y = y;
  isSubStreamPositionInitial.value = true;
};
// 確定 scroll bar 出現才開始顯示，不然會造成定位右下角不准的 issue
whenever(
  () => isBodyMounted.value && shouldShowSubStreamRoom.value,
  () => nextTick(initSubStreamPosition),
  { immediate: true },
);

const shouldShowAudioIcon = computed(() => {
  return isOnLive.value || isReplaying.value;
});
const isMute = ref(true);
onAudioStatusChanged((value: boolean) => { isMute.value = value; });

const shouldShowPlayIcon = computed(() => {
  return isReplaying.value;
});
const isPlay = ref(false);
onPlayStatusChanged((value: boolean) => {
  isPlay.value = value;
});

const mainStreamStyle = computed(() => {
  if (streamElBounding.value === null) {
    return 'display:none;';
  }

  const { x, y, height, width } = streamElBounding.value;
  return `left:${x}px;top:${y}px;height:${height}px;width:${width}px;`;
});
// 計算拖移的位置，並防止使用者拖移超出視窗
const subStreamStyle = computed(() => {
  if (isSubStreamHide.value || !isSubStreamPositionInitial.value) {
    return 'display: none;';
  }

  const edge = {
    x: bodySize.width.value - subStreamSize.value.width,
    y: windowSize.height.value - subStreamSize.value.height,
  };

  const dragPosition = wrapperDraggable.value.position;
  const x = clamp(dragPosition.x, 0, edge.x);
  const y = clamp(dragPosition.y, 0, edge.y);
  return `left:${x}px;top:${y}px;`;
});

const wrapperStyle = computed(() => {
  return shouldShowSubStreamRoom.value ? subStreamStyle.value : mainStreamStyle.value;
});

// NOTE: hasMoved 是為了讓拖移時不觸發其它點擊功能
const closeSubStreamRoom = () => {
  if (hasMoved.value) {
    hasMoved.value = false;
    return;
  }

  shouldShowSubStreamRoom.value = false;
  isSubStreamHide.value = true;
  if (streamEl.value) {
    streamRoomIntersectionObserver.unobserve(streamEl.value);
  }
};

const postShareUrl = computed(() => handsupConfig.value?.post?.share_url);
const shareUrl = computed(() => {
  return postShareUrl.value ?? liveRoomUrl.value;
});
const goToStreamRoom = () => {
  if (hasMoved.value) {
    hasMoved.value = false;
    return;
  }

  const { isPipOnly } = globalConfig.value;
  if (isPipOnly) {
    // NOTE: isPipOnly 時，開新分頁連到商店設定的 share_url
    // 若無設定 share_url 則回到 buyer stream room
    window.open(shareUrl.value, '_blank');
  } else {
    if (!streamElBounding.value) { return; }
    
    const SPACING_TO_WINDOW_BOTTOM = 20;
    const { y: streamElY, height: streamElHeight } = streamElBounding.value;
    const windowHeight = window.innerHeight;
    window.scrollTo({
      top: streamElY - (windowHeight - streamElHeight) + SPACING_TO_WINDOW_BOTTOM,
      behavior: 'smooth',
    });
  }
};

const clickAudio = () => {
  if (hasMoved.value) {
    hasMoved.value = false;
    return;
  }

  requestMute(!isMute.value);
};

const clickPlay = () => {
  if (hasMoved.value) {
    hasMoved.value = false;
    return;
  }

  requestPlay(!isPlay.value);
};

onChapterClickedToggle((seekTime: number) => requestSeek(seekTime));

let haveSharingSeek = false;
onMounted(() => {
  const handsupSharingSeekTime = UrlQueryConsumer.consume(KNOWN_CONSUMABLES.SHARING_SEEK);
  if (handsupSharingSeekTime) {
    haveSharingSeek = true;
    requestSeek(Number(handsupSharingSeekTime));
  }
});

// 避免手機拖移時滑動 scrollbar
const { enable: enableLocker, disable: disableLocker } = useScrollLocker();
useEventListener(wrapperEl, 'pointerdown', enableLocker);
useEventListener(wrapperEl, 'pointerup', disableLocker);
onClickOutside(wrapperEl, disableLocker);

// #region inactivity timer
const TIMEOUT_IN_MS = secondsToMilliseconds(3);
const isStreamPipActive = ref(false);

const { start: startInactivityTimer, stop: stopInactivityTimer } = useTimeoutFn(() => {
  isStreamPipActive.value = false;
}, TIMEOUT_IN_MS);

const activateInactivityTimer = () => {
  isStreamPipActive.value = true;
  stopInactivityTimer();
  startInactivityTimer();
};
// #endregion inactivity timer

const calcStreamPipClass = computed(() => ({
  'stream-pip--sub-stream': shouldShowSubStreamRoom.value,
  'stream-pip--active': isStreamPipActive.value,
  'video-play': isPlay.value,
  'video-pause': !isPlay.value,
}));

// #region record last view time into localstorage
let lastViewedStorageProxy: { value: { value: any }; setValue: (value: number) => void } | null = null;
const throttledSetLastViewed = throttle((time: number) => {
  lastViewedStorageProxy?.setValue(time);
}, 1000);

/** @type { import('vue').Ref<import('@/js/api').ArchiveClipsData | null> } */
const selectedClip = ref(null);
onSelectedClipChanged((clip: any) => {
  selectedClip.value = clip;
});

const videoCurrentTime = ref(0);
onVideoCurrentTimeChanged((time: number) => {
  videoCurrentTime.value = time;
});
watch(videoCurrentTime, () => {
  if (!lastViewedStorageProxy) { return; }
  throttledSetLastViewed(videoCurrentTime.value);
});

const lastViewedKey = computed(() => {
  if (!postId.value || !isReplaying.value) { return null; }

  if (isClipArchive.value && !selectedClip.value) { return null; }
  return isClipArchive.value && selectedClip.value ? `${postId.value}_${(selectedClip.value as any).id}` : `${postId.value}`;
});

watch(lastViewedKey, () => {
  if (!lastViewedKey.value) {
    lastViewedStorageProxy = null;
    return;
  }

  lastViewedStorageProxy = useExpirableLocalStorage(`lastViewedTime_${lastViewedKey.value}`, 0, 24 * 60 * 60 * 1000);
  const lastViewTime = lastViewedStorageProxy.value.value;
  if (!haveSharingSeek && lastViewTime) {
    requestSeek(lastViewTime);
  }
});
// #endregion record last view time into localstorage
</script>

<template>
<!---
  :style 包含控制 display: none;，因為 v-show 跟 :style 有覆蓋情況
-->
<div
  class="stream-pip w-full h-full select-none"
  v-if="needRender"
  ref="wrapperEl"
  :style="wrapperStyle"
  :class="calcStreamPipClass"
  @click="activateInactivityTimer"
>
  <div
    class="stream-pip__content w-full h-full"
    v-show="isShowMainStreamRoom || isShowSubStreamRoom"
  >
    <!---
      無法監聽 iframe 的 event，所以蓋上一層，但會造成 iframe 內的東西都無法點擊,
      可以透過這個 demo 測試: https://jsfiddle.net/ro1963855/gf15vq48/2/，
      另外需要加上 z-index 來保證這一層永遠都會蓋在 iframe 上面，不然會造成無法拖移等問題
    -->

    <div class="w-full h-full absolute cursor-move z-10" v-show="shouldShowSubStreamRoom">
      <!---
        NOTE: click 的 stop 是為了防止 PIP 關掉後，點到後方的轉導頁面元素
        NOTE: 換成 pointerup 會造成無法阻擋頁面轉導的 issue，與事件觸發順序相關
        REF: https://www.w3.org/TR/pointerevents3/#mapping-for-devices-that-do-not-support-hover
      -->
      <div
        class="stream-pip__close"
        v-show="shouldShowSubStreamRoom"
        @click.stop="closeSubStreamRoom"
      >
        <IconHumbleiconsTimes class="text-base"/>
      </div>

      <div
        class="stream-pip__scroll"
        v-show="shouldShowSubStreamRoom"
        @click="goToStreamRoom"
      >
        <InlineSvg class="scroll__icon" :src="ICON_BACK_TO_STREAM_ROOM"/>
      </div>

      <div
        class="stream-pip__volume"
        v-show="shouldShowAudioIcon && shouldShowSubStreamRoom"
        @click="clickAudio"
      >
        <InlineSvg class="stream-pip__volume__icon" v-if="isMute" :src="ICON_AUDIO_OFF"/>
        <InlineSvg class="stream-pip__volume__icon" v-else :src="ICON_AUDIO_ON"/>
      </div>

      <div
        class="stream-pip__play"
        v-show="shouldShowPlayIcon && shouldShowSubStreamRoom"
        @click="clickPlay"
      >
        <InlineSvg class="stream-pip__play__icon" v-if="isPlay" :src="ICON_PAUSE"/>
        <InlineSvg class="stream-pip__play__icon" v-else :src="ICON_PLAY"/>
      </div>
    </div>

    <!---
      REF: https://developer.mozilla.org/en-US/docs/Web/HTML/Element/iframe
      REF: https://developer.mozilla.org/en-US/docs/Web/HTTP/Headers/Feature-Policy
      NOTE: 如果沒設定 `allow="autoplay;"` 就開關 iframe 的影片聲音會跳 exception，原因是沒跟 iframe 裡的元素互動過，設定後可以解決此問題
    -->
    <iframe
      class="w-full h-full"
      :src="liveRoomUrl"
      allow="autoplay; clipboard-read; clipboard-write"
      scrolling="no"
    ></iframe>
  </div>
  <div
    class="absolute inset-0 flex justify-center items-center bg-black pointer-events-none"
    v-if="!(isShowMainStreamRoom || isShowSubStreamRoom)"
  >
    <IconFa6SolidSpinner class="stream-pip__spinner text-white text-3xl"/>
  </div>
</div>
</template>
<style lang="scss" scoped>
// 參考 handsup-live-sdk 的長寬

// 跟 ＠/components/StreamPip/StreamPip.vue 的設定相同，若要修改要同時修改兩邊
$sub-video-width-sm: 150px;
$sub-video-height-sm: 263px;
$sub-video-width-md: 180px;
$sub-video-height-md: 315px;

.stream-pip {
  @apply absolute top-0 left-0;

  touch-action: none;

  &--sub-stream {
    @apply fixed rounded-lg overflow-hidden w-auto h-auto z-9999;

    width: $sub-video-width-sm;
    height: $sub-video-height-sm;
    box-shadow: 0 0 8px rgba(0, 0, 0, 0.1);

    @screen md {
      width: $sub-video-width-md;
      height: $sub-video-height-md;
    }
  }

  &__close {
    @apply absolute top-1 right-1 cursor-pointer w-6 h-6 text-base text-white;
    @apply flex justify-center items-center;
    @apply bg-black bg-opacity-20 rounded-full border border-gray-500;

    -webkit-tap-highlight-color: transparent;

    @include rwd-hover {
      @apply bg-opacity-70;
    }
  }

  &__scroll {
    @apply absolute top-1 left-1 w-6 h-6 cursor-pointer;
    @apply flex justify-center items-center;
    @apply bg-black bg-opacity-20 rounded-full border border-gray-500;

    -webkit-tap-highlight-color: transparent;

    @include rwd-hover {
      @apply bg-opacity-70;
    }

    &__icon {
      @apply w-4;
    }
  }

  &__play {
    @apply absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 cursor-pointer;
    @apply flex justify-center items-center;
    @apply rounded-full border w-12 h-12 bg-black bg-opacity-20;

    border-color: #E5E5E5;
    -webkit-tap-highlight-color: transparent;

    @include rwd-hover {
      @apply bg-opacity-70;
    }

    &__icon {
      @apply w-6 h-6 fill-white;
    }
  }

  &__volume {
    @apply absolute bottom-1 right-1 cursor-pointer;
    @apply flex justify-center items-center;
    @apply rounded-full border w-6 h-6 bg-black bg-opacity-20;

    border-color: #E5E5E5;
    -webkit-tap-highlight-color: transparent;

    @include rwd-hover {
      @apply bg-opacity-70;
    }

    &__icon {
      @apply w-4 h-4 fill-white;
    }
  }

  @media (pointer: fine) {
    &__close,
    &__scroll,
    &__play,
    &__volume {
      @apply transition-opacity duration-300;
      @apply opacity-0;
    }

    &.video-pause {
      .stream-pip__play {
        @apply opacity-100;
      }
    }

    &.video-play {
      .stream-pip__volume {
        @apply opacity-100;
      }
    }

    &:hover {
      .stream-pip {
        &__close,
        &__scroll,
        &__play,
        &__volume {
          @apply opacity-100;
        }
      }
    }
  }

  @media (pointer: coarse) {
    &__close,
    &__scroll,
    &__play,
    &__volume {
      @apply transition-opacity duration-300;
      @apply opacity-0;
    }

    &.video-pause {
      .stream-pip__play {
        @apply opacity-100;
      }
    }

    &.video-play {
      .stream-pip__volume {
        @apply opacity-100;
      }
    }

    &.stream-pip--active {
      .stream-pip {
        &__close,
        &__scroll,
        &__play,
        &__volume {
          @apply opacity-100;
        }
      }
    }
  }

  &__spinner {
    animation: spin-animation 1s steps(8) infinite;
  }

  @keyframes spin-animation {
    from {
      transform: rotate(0deg);
    }

    to {
      transform: rotate(1turn);
    }
  }
}
</style>
